import React, {useEffect, useMemo, useState} from "react";
import axios from "config/axiosConfig";
import {formatCurrency} from "../util/helpers";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";

const statusOptions = [
    { value: "", label: "모두" },
    { value: "REQUESTED", label: "정산 요청" },
    { value: "COMPLETED", label: "정산 완료" },
    { value: "REJECTED", label: "정산 실패" },
];

const displayStatus = (status) => {
    switch (status) {
        case "REQUESTED": return "정산 요청";
        case "COMPLETED": return "정산 완료";
        case "REJECTED": return "정산 실패";
        default: return status || "-";
    }
};

const parseIds = (value) => {
    if (!value) return [];
    try {
        return JSON.parse(value);
    } catch (e) {
        return [];
    }
};

export default function WithdrawalSummary() {
    const [status, setStatus] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const load = async () => {
        try {
            setLoading(true);
            const params = {};
            if (status) params.status = status;
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            const res = await axios.get("/admin/v1/wallet/summarize-withdrawals", { params });
            setRows(res.data || []);
            setError(null);
        } catch (err) {
            setRows([]);
            setError(err?.response?.data?.detail || "정산 요약 정보를 불러오는 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        try {
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await axios.get("/admin/v1/wallet/withdrawals/export-xls", {
                params,
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });

            // Derive filename from Content-Disposition or build fallback
            let filename = null;
            const cd = response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
            if (cd) {
                const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
                if (match) {
                    filename = decodeURIComponent(match[1] || match[2]);
                }
            }
            if (!filename) {
                const fmt = (d) => dayjs(d).format('YYMMDD');
                const today = dayjs();
                const startStr = startDate ? fmt(startDate) : fmt(today);
                const endStr = endDate ? fmt(endDate) : fmt(today);
                filename = `대량이체_${startStr}_${endStr}.xls`;
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(err?.response?.data?.detail || "엑셀 생성 중 오류가 발생했습니다.");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const aggregates = useMemo(() => {
        const totalAmount = rows.reduce((sum, row) => sum + (row.totalAmount || 0), 0);
        const totalWithdrawalCount = rows.reduce((sum, row) => sum + parseIds(row.withdrawalIds).length, 0);
        return { totalAmount, totalWithdrawalCount };
    }, [rows]);

    return (
        <div className="App">
            <div className="wrapper">
                <div className="content-wrapper" style={{marginLeft: '0px'}}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-12">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item active">결제관리 &gt; 정산관리(묶음)</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <div className="card-tools" style={{marginTop: '40px'}}>
                                                <div className="form-group row" style={{float: "right"}}>
                                                    <label className="col-sm-auto col-form-label" style={{marginLeft: '10px'}}>상태</label>
                                                    <select className="form-control float-right"
                                                            style={{width: '160px', marginRight: '10px'}}
                                                            onChange={e => setStatus(e.target.value)}
                                                            value={status}>
                                                        {statusOptions.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>

                                                    <label className="col-sm-auto col-form-label" style={{marginLeft: '10px'}}>시작일</label>
                                                    <input type="date" className="form-control float-right"
                                                           style={{width: '160px', marginRight: '10px'}}
                                                           value={startDate || ""}
                                                           onChange={e => setStartDate(e.target.value)} />
                                                    <label className="col-sm-auto col-form-label" style={{marginLeft: '10px'}}>종료일</label>
                                                    <input type="date" className="form-control float-right"
                                                           style={{width: '160px', marginRight: '10px'}}
                                                           value={endDate || ""}
                                                           onChange={e => setEndDate(e.target.value)} />

                                                    <button type="button" className="btn btn-info btn-sm"
                                                            style={{width: '80px', marginLeft: '10px'}}
                                                            onClick={load}>
                                                        검색
                                                    </button>
                                                    <button type="button" className="btn btn-success btn-sm"
                                                            style={{marginLeft: '10px'}}
                                                            onClick={handleDownloadExcel}>
                                                        대량이체엑셀다운로드
                                                    </button>
                                                </div>
                                            </div>
                                            {/*<div className="mt-3">*/}
                                            {/*    <span className="mr-4">*/}
                                            {/*        총 금액: {aggregates.totalAmount === 0 ? "0" : formatCurrency(aggregates.totalAmount)}원*/}
                                            {/*    </span>*/}
                                            {/*    <span>*/}
                                            {/*        총 출금 건수: {aggregates.totalWithdrawalCount}*/}
                                            {/*    </span>*/}
                                            {/*</div>*/}
                                        </div>
                                        <div className="card-body table-responsive p-0">
                                            {error && (
                                                <div className="alert alert-danger m-3" role="alert">{error}</div>
                                            )}
                                            {!error && (
                                                <table className="table table-hover text-nowrap" style={{textAlign: "center"}}>
                                                    <thead>
                                                    <tr>
                                                        <th>출금 요청 ID</th>
                                                        <th>결제 ID</th>
                                                        <th>회원ID</th>
                                                        <th>정산상태</th>
                                                        <th>지갑ID</th>
                                                        <th>정산 신청일</th>
                                                        <th>총 금액</th>
                                                        <th>출금 건수</th>
                                                        <th>액션</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {rows.map((row, idx) => {
                                                        const withdrawalIds = parseIds(row.withdrawalIds);
                                                        const paymentIds = parseIds(row.paymentIds);
                                                        return (
                                                            <tr key={`${row.memberId}-${row.status}-${idx}`}>
                                                                <td style={{textAlign: "center"}}>
                                                                    {withdrawalIds.length > 0 ? withdrawalIds.map((wid, index) => (
                                                                        <React.Fragment key={wid}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link btn-sm p-0 mr-1"
                                                                                onClick={() => window.open(`/page/payments/withdrawals/detail?id=${wid}`, '_blank', 'noopener')}
                                                                            >
                                                                                {wid}
                                                                            </button>
                                                                            {index !== withdrawalIds.length - 1 && ", "}
                                                                        </React.Fragment>
                                                                    )) : "-"}
                                                                </td>
                                                                <td style={{textAlign: "center"}}>
                                                                    {paymentIds.length > 0 ? paymentIds.map((pid, index) => (
                                                                        <React.Fragment key={pid}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link btn-sm p-0 mr-1"
                                                                                onClick={() => window.open(`/page/payments/detail?id=${pid}`, '_blank', 'noopener')}
                                                                            >
                                                                                {pid}
                                                                            </button>
                                                                            {index !== paymentIds.length - 1 && ", "}
                                                                        </React.Fragment>
                                                                    )) : "-"}
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link btn-sm p-0"
                                                                        onClick={() => window.open(`/page/users/detail?userId=${row.memberId}`, '_blank', 'noopener')}
                                                                    >
                                                                        {row.memberId}
                                                                    </button>
                                                                </td>
                                                                <td>{displayStatus(row.status)}</td>
                                                                <td>{row.walletId}</td>
                                                                <td>{row.requestDate}</td>
                                                                <td>{row.totalAmount === 0 ? "0" : formatCurrency(row.totalAmount)}</td>
                                                                <td>{withdrawalIds.length}</td>
                                                                <td>
                                                                    {withdrawalIds.length > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-primary btn-sm"
                                                                            onClick={() => {
                                                                                const searchParams = new URLSearchParams();
                                                                                searchParams.set("memberId", row.memberId);
                                                                                if (row.status) searchParams.set("status", row.status);
                                                                                if (row.walletId) searchParams.set("walletId", row.walletId);
                                                                                if (row.requestDate) searchParams.set("requestDate", row.requestDate);
                                                                                if (startDate) searchParams.set("startDate", startDate);
                                                                                if (endDate) searchParams.set("endDate", endDate);
                                                                                navigate(`/page/payments/withdrawals/summary/detail?${searchParams.toString()}`,
                                                                                    { state: { summaryRow: row, filters: { status, startDate, endDate } } }
                                                                                );
                                                                            }}
                                                                        >
                                                                            보기
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {rows.length === 0 && !loading && (
                                                        <tr>
                                                            <td colSpan={9}>조회된 정산 묶음이 없습니다.</td>
                                                        </tr>
                                                    )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
